import React, { useState, useEffect } from 'react';
import {
  Send, Plus, Trash2, Calendar,
  Clock, Phone, User, Tag,
  MessageSquare, CheckCircle2, Loader2,
  AlertCircle, List, Filter, ShieldAlert, Zap, Bookmark
} from 'lucide-react';
import { lembreteService, usuarioService } from '../services/api'; // Certifique-se de importar usuarioService
import { auth } from '../config/firebase';

const StatusBadge = ({ enviado, status, dataHoraDisparo }) => {
  const dataDisparo = new Date(dataHoraDisparo);
  const agora = new Date();
  if (status) {
    if (status === 'ENVIADO') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold text-[#059669] bg-[#ecfdf5]"><CheckCircle2 size={12} /> ENVIADO</span>;
    if (status === 'FALHA') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold text-[#e11d48] bg-[#fff1f2]"><AlertCircle size={12} /> FALHOU</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold text-[#d97706] bg-[#fffbeb]"><Clock size={12} /> PENDENTE</span>;
  }
  if (enviado) return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold text-[#059669] bg-[#ecfdf5]"><CheckCircle2 size={12} /> ENVIADO</span>;
  if (!enviado && dataDisparo < agora) return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold text-[#e11d48] bg-[#fff1f2]"><AlertCircle size={12} /> FALHOU</span>;
  return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-[10px] font-bold text-[#d97706] bg-[#fffbeb]"><Clock size={12} /> PENDENTE</span>;
};

// Aplica a mesma máscara de (DD) DDDDD-DDDD usada no campo de telefone, a partir de um
// telefone já cadastrado (ex: vindo da aba Clientes, geralmente só com dígitos).
const formatarTelefoneParaExibicao = (raw) => {
  let value = (raw || '').replace(/\D/g, '');
  if (value.length > 11 && value.startsWith('55')) value = value.slice(2);
  if (value.length > 11) value = value.slice(0, 11);
  if (value.length > 2) value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
  if (value.length > 10) value = `${value.slice(0, 10)}-${value.slice(10)}`;
  return value;
};

const CriarLembreteAvulso = ({ clientePreSelecionado } = {}) => {
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [tipoAgendamento, setTipoAgendamento] = useState('unico');
  const [lembretes, setLembretes] = useState([]);
  const [loadingLista, setLoadingLista] = useState(true);
  const [filtroData, setFiltroData] = useState('');

  // Controle de Plano para o Limite
  const [planoUsuario, setPlanoUsuario] = useState('COMUM');

  // Modelos salvos pelo próprio profissional (persistidos no banco, diferente das sugestões
  // fixas abaixo, que são só um ponto de partida igual pra todo mundo).
  const [templatesSalvos, setTemplatesSalvos] = useState([]);
  const [salvandoTemplate, setSalvandoTemplate] = useState(false);

  const [formData, setFormData] = useState({
    nome: clientePreSelecionado?.nome || '',
    telefone: formatarTelefoneParaExibicao(clientePreSelecionado?.telefone),
    servico: '',
    mensagem_customizada: ''
  });
  const [datas, setDatas] = useState([{ id: Date.now(), data: '', hora_disparo: '', hora_agendamento: '' }]);

  // Lógica de Limite Diário Dinâmica
  const LIMITE_DIARIO = planoUsuario === 'EMPRESARIAL' ? 100 : 20;
  const [enviosHoje, setEnviosHoje] = useState(0);

  const MENSAGENS_PREDEFINIDAS = [
    { titulo: "Cliente Sumido", texto: "Vixe, já faz um tempo desde o nosso último atendimento de {servico}... tá precisando dar uma geral essa semana? me avisa aqui pra gente combinar o dia! 😉" },
    { titulo: "Retorno de Rotina", texto: "Opa, {nome}! passando pra lembrar que já tá no tempo do seu retorno para fazer seu atendimento de {servico}. vamos deixar logo marcado para garantir sua vaga?" },
    { titulo: "Lembrete com Confirmação", texto: "Ei, {nome}, tudo bem? passando pra confirmar nosso atendimento de {servico} marcado para o dia {data} às {hora}. consegue me dar um ok aqui só pra eu deixar garantido na agenda? valeu!" },
    { titulo: "Aviso Importante", texto: "Olá! passando rapidinho pra te avisar sobre o seu atendimento de {servico}. se precisar remarcar ou tiver alguma dúvida, é só me mandar mensagem por aqui, tá?" }
  ];

  useEffect(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const contagem = lembretes.filter(l => {
      if (!l.data_hora_disparo) return false;
      const dataLembrete = new Date(l.data_hora_disparo);
      dataLembrete.setHours(0, 0, 0, 0);
      const statusSeguro = l.status ? l.status.toUpperCase() : '';
      return dataLembrete.getTime() === hoje.getTime() && statusSeguro !== 'FALHA';
    }).length;
    setEnviosHoje(contagem);
  }, [lembretes]);

  useEffect(() => {
    const carregarHistoricoEPerfil = async (user) => {
      try {
        setLoadingLista(true);
        const token = await user.getIdToken();
        
        const [resPerfil, resLembretes, resTemplates] = await Promise.all([
          usuarioService.obterPerfil(user.uid).catch(() => ({ data: { plano: 'COMUM' } })),
          lembreteService.listar(token).catch(() => ({ data: [] })),
          lembreteService.listarTemplates(token).catch(() => ({ data: [] }))
        ]);

        setPlanoUsuario(resPerfil.data?.plano || 'COMUM');
        setLembretes(resLembretes.data || []);
        setTemplatesSalvos(resTemplates.data || []);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoadingLista(false);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) carregarHistoricoEPerfil(user);
      else setLoadingLista(false);
    });

    return () => unsubscribe();
  }, []);

  const handleTelefoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) value = value.slice(0, 11);
    if (value.length > 2) value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
    if (value.length > 10) value = `${value.slice(0, 10)}-${value.slice(10)}`;
    setFormData({ ...formData, telefone: value });
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSelecionarMensagemPredefinida = (e) => {
      const valorSelecionado = e.target.value;
      if (valorSelecionado !== "") {
        let textoProcessado = valorSelecionado;
        if (formData.nome) textoProcessado = textoProcessado.replace(/{nome}/gi, formData.nome.split(' ')[0]);
        if (formData.servico) textoProcessado = textoProcessado.replace(/{servico}/gi, formData.servico);
        setFormData({ ...formData, mensagem_customizada: textoProcessado });
      }
  };

  const handleSalvarComoModelo = async () => {
    if (!formData.mensagem_customizada.trim()) {
      return alert('Escreva a mensagem antes de salvar como modelo.');
    }
    const titulo = window.prompt('Dê um nome pra esse modelo (ex: "Confirmação de horário"):');
    if (!titulo || !titulo.trim()) return;

    setSalvandoTemplate(true);
    try {
      const token = await auth.currentUser.getIdToken();
      await lembreteService.criarTemplate({ titulo, mensagem: formData.mensagem_customizada }, token);
      const res = await lembreteService.listarTemplates(token);
      setTemplatesSalvos(res.data || []);
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao salvar o modelo.');
    } finally {
      setSalvandoTemplate(false);
    }
  };

  const handleExcluirTemplate = async (id) => {
    if (!window.confirm('Excluir esse modelo salvo?')) return;
    try {
      const token = await auth.currentUser.getIdToken();
      await lembreteService.excluirTemplate(id, token);
      setTemplatesSalvos((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      alert(error.response?.data?.message || 'Erro ao excluir o modelo.');
    }
  };

  const adicionarData = () => {
    if (enviosHoje + datas.length >= LIMITE_DIARIO) {
        alert("Você atingirá o limite diário de envios se adicionar mais uma sessão.");
        return;
    }
    setDatas([...datas, { id: Date.now(), data: '', hora_disparo: '', hora_agendamento: '' }]);
  };

  const removerData = (id) => {
    if (datas.length === 1) return; 
    setDatas(datas.filter(d => d.id !== id));
  };

  const atualizarData = (id, campo, valor) => {
    setDatas(datas.map(d => d.id === id ? { ...d, [campo]: valor } : d));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviosHoje + datas.length > LIMITE_DIARIO) {
      alert(`Limite diário excedido. Você só pode agendar mais ${LIMITE_DIARIO - enviosHoje} lembretes.`);
      return;
    }

    if (datas.some(d => !d.data || !d.hora_disparo) || !formData.telefone || !formData.servico) {
      return alert("Por favor, preencha todos os campos obrigatórios.");
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken();
      const payload = {
        telefone: formData.telefone.replace(/\D/g, ''),
        nome: formData.nome,
        servico: formData.servico,
        mensagem_customizada: formData.mensagem_customizada, 
        agendamentos: datas.map(d => ({ data: d.data, hora_disparo: d.hora_disparo, hora_agendamento: d.hora_agendamento || "" }))
      };

      await lembreteService.agendar(payload, token);
      setSucesso(true);
      
      const res = await lembreteService.listar(token);
      setLembretes(res.data);

      setTimeout(() => {
        setSucesso(false);
        setFormData({ nome: '', telefone: '', servico: '', mensagem_customizada: '' }); 
        setDatas([{ id: Date.now(), data: '', hora_disparo: '', hora_agendamento: '' }]);
        setTipoAgendamento('unico');
      }, 3000);

    } catch (error) {
      alert(error.response?.data?.message || "Erro ao agendar lembrete.");
    } finally {
      setLoading(false);
    }
  };

  const atingiuLimite = enviosHoje >= LIMITE_DIARIO;
  const dataPreview = datas[0].data ? new Date(datas[0].data + 'T12:00:00').toLocaleDateString('pt-BR') : '[DATA]';
  const horaPreview = datas[0].hora_agendamento || '--:--';
  const nomePreview = formData.nome ? formData.nome.split(' ')[0] : 'Cliente';
  const servicoPreview = formData.servico || '[SERVIÇO]';

  const obterTextoPreview = () => {
    if (formData.mensagem_customizada && formData.mensagem_customizada.trim() !== "") {
      let texto = formData.mensagem_customizada;
      texto = texto.replace(/{nome}/gi, nomePreview);
      texto = texto.replace(/{servico}/gi, servicoPreview);
      texto = texto.replace(/{data}/gi, dataPreview);
      texto = texto.replace(/{hora}/gi, horaPreview);
      if (!formData.mensagem_customizada.includes('{data}') && !formData.mensagem_customizada.includes('{hora}')) {
        texto += `\n\n*Sugestão de Agendamento:* ${servicoPreview}\n*Data:* ${dataPreview} às ${horaPreview}`;
      }
      return texto;
    }
    return `Olá ${nomePreview}! Passando para lembrar do seu agendamento de ${servicoPreview} para o dia ${dataPreview} às ${horaPreview}...`;
  };

  const lembretesFiltrados = lembretes.filter(lembrete => {
    if (!filtroData) return true;
    return new Date(lembrete.data_hora_disparo).toLocaleDateString('en-CA') === filtroData;
  });

  const modoCompacto = !!clientePreSelecionado;

  return (
    <div className={modoCompacto ? 'w-full flex flex-col gap-6 font-sans box-border' : 'w-full max-w-[800px] mx-auto px-1 flex flex-col gap-6 font-sans box-border'}>
      <div className={modoCompacto ? 'w-full box-border text-left' : 'bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.05)] box-border w-full overflow-hidden text-left'}>
        {!modoCompacto && (
          <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="flex items-center gap-2.5 text-lg font-black text-slate-900 m-0"><Send size={20} className="text-[#1883FF]" /> Agendar Lembrete Personalizado</h2>
              <p className="text-xs text-slate-400 font-medium mt-1">Configure notificações separando o horário de envio do horário da agenda.</p>
            </div>
          </div>
        )}

        {/* BANNER DINÂMICO DE LIMITES ADAPTADO AO PLANO */}
        <div className={`flex items-center justify-between flex-wrap p-3 px-4 rounded-xl mb-6 gap-3 w-full box-border border ${atingiuLimite ? 'bg-red-50 border-red-200' : 'bg-[#F8F7FF] border-purple-50'}`}>
          <div className={`flex items-center gap-2.5 text-xs font-bold ${atingiuLimite ? 'text-red-600' : 'text-[#6D28D9]'}`}>
            {atingiuLimite ? <ShieldAlert size={18} /> : <AlertCircle size={18} />}
            <span>{atingiuLimite ? 'Limite diário atingido!' : `Limite Diário: ${enviosHoje}/${LIMITE_DIARIO} disparos`}</span>
            {planoUsuario !== 'EMPRESARIAL' && !atingiuLimite && (
               <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-md text-[8px] uppercase font-black ml-2">Plano Comum</span>
            )}
          </div>
          <div className="w-full sm:w-[150px] h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${atingiuLimite ? 'bg-red-500' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9]'}`}
              style={{ width: `${Math.min((enviosHoje / LIMITE_DIARIO) * 100, 100)}%` }}
            ></div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <fieldset disabled={atingiuLimite || loading} className="border-none p-0 m-0 w-full flex flex-col disabled:opacity-60">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5 w-full">
              <div className="flex flex-col gap-1.5 w-full box-border">
                <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5 whitespace-nowrap"><Phone size={12} /> WhatsApp do Cliente *</label>
                <input type="text" className="w-full bg-[#F8F7FF] border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 box-border" placeholder="(00) 00000-0000" value={formData.telefone} onChange={handleTelefoneChange} required />
              </div>
              <div className="flex flex-col gap-1.5 w-full box-border">
                <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5 whitespace-nowrap"><User size={12} /> Nome (Opcional)</label>
                <input type="text" className="w-full bg-[#F8F7FF] border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 box-border" name="nome" placeholder="Ex: João" value={formData.nome} onChange={handleInputChange} />
              </div>
              <div className="flex flex-col gap-1.5 sm:col-span-2 w-full box-border">
                <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5 whitespace-nowrap"><Tag size={12} /> Serviço/Procedimento *</label>
                <input type="text" className="w-full bg-[#F8F7FF] border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 box-border" name="servico" placeholder="Ex: Limpeza Dental..." value={formData.servico} onChange={handleInputChange} required />
              </div>

              <div className="flex flex-col gap-1.5 sm:col-span-2 w-full box-border">
                <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5 whitespace-nowrap"><MessageSquare size={12} /> Escolher Mensagem Pronta (Opcional)</label>
                <select className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-semibold text-slate-700 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 box-border cursor-pointer" onChange={handleSelecionarMensagemPredefinida} defaultValue="">
                  <option value="">-- Selecione um modelo pronto para preencher abaixo --</option>
                  {templatesSalvos.length > 0 && (
                    <optgroup label="Meus modelos salvos">
                      {templatesSalvos.map((tpl) => <option key={tpl.id} value={tpl.mensagem}>{tpl.titulo}</option>)}
                    </optgroup>
                  )}
                  <optgroup label="Sugestões">
                    {MENSAGENS_PREDEFINIDAS.map((msg, idx) => <option key={idx} value={msg.texto}>{msg.titulo}</option>)}
                  </optgroup>
                </select>
                {templatesSalvos.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {templatesSalvos.map((tpl) => (
                      <span key={tpl.id} className="inline-flex items-center gap-1 bg-white border border-purple-100 text-[#6D28D9] text-[10px] font-bold px-2.5 py-1 rounded-lg">
                        {tpl.titulo}
                        <button type="button" onClick={() => handleExcluirTemplate(tpl.id)} className="bg-transparent border-none p-0 cursor-pointer text-slate-300 hover:text-red-500 flex items-center" title="Excluir modelo">
                          <Trash2 size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5 sm:col-span-2 w-full box-border">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5 whitespace-nowrap"><MessageSquare size={12} /> Mensagem Customizada</label>
                  <button type="button" onClick={handleSalvarComoModelo} disabled={salvandoTemplate} className="flex items-center gap-1 bg-transparent border-none text-[10px] font-black uppercase text-[#6D28D9] cursor-pointer hover:underline disabled:opacity-50 disabled:cursor-not-allowed px-0">
                    {salvandoTemplate ? <Loader2 size={11} className="animate-spin" /> : <Bookmark size={11} />} Salvar como modelo
                  </button>
                </div>
                <textarea className="w-full bg-[#F8F7FF] border border-purple-50 py-3 px-4 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 min-h-[90px] resize-y font-sans box-border" name="mensagem_customizada" placeholder="Selecione um modelo acima ou digite seu text livre por aqui..." value={formData.mensagem_customizada} onChange={handleInputChange}/>
              </div>
            </div>

            <div className="flex bg-slate-100 p-1 rounded-xl mb-6 w-full box-border">
              <button type="button" className={`flex-1 py-2.5 text-[11px] font-black uppercase tracking-wider border-none rounded-lg cursor-pointer transition-all ${tipoAgendamento === 'unico' ? 'bg-white text-[#6D28D9] shadow-sm' : 'bg-transparent text-slate-400'}`} onClick={() => { setTipoAgendamento('unico'); setDatas([datas[0]]); }}>Único</button>
              <button type="button" className={`flex-1 py-2.5 text-[11px] font-black uppercase tracking-wider border-none rounded-lg cursor-pointer transition-all ${tipoAgendamento === 'pacote' ? 'bg-white text-[#6D28D9] shadow-sm' : 'bg-transparent text-slate-400'}`} onClick={() => setTipoAgendamento('pacote')}>Pacote / Sessões</button>
            </div>

            <div className="bg-[#F8F7FF] border border-purple-50 rounded-2xl p-3 sm:p-5 mb-6 w-full box-border">
              {datas.map((item, index) => (
                <div key={item.id} className="flex flex-col sm:flex-row sm:items-end gap-3.5 mb-5 pb-5 border-b border-dashed border-purple-100 last:mb-0 last:pb-0 last:border-b-0 w-full box-border">
                  <div className="flex flex-col gap-1.5 w-full sm:flex-1 box-border text-left">
                    <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><Calendar size={12} /> Data {tipoAgendamento === 'pacote' && `#${index + 1}`}</label>
                    <input type="date" className="w-full bg-white border border-purple-50 py-3 px-3 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 box-border" value={item.data} onChange={(e) => atualizarData(item.id, 'data', e.target.value)} required />
                  </div>
                  <div className="flex flex-col gap-1.5 w-full sm:flex-1 box-border text-left">
                    <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><Zap size={12} /> Disparo</label>
                    <input type="time" className="w-full bg-white border border-purple-50 py-3 px-3 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 box-border" value={item.hora_disparo} onChange={(e) => atualizarData(item.id, 'hora_disparo', e.target.value)} required />
                  </div>
                  <div className="flex flex-col gap-1.5 w-full sm:flex-1 box-border text-left">
                    <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><Clock size={12} /> Agenda</label>
                    <input type="time" className="w-full bg-white border border-purple-50 py-3 px-3 rounded-xl text-sm font-semibold text-slate-900 outline-none transition-all focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10 box-border" value={item.hora_agendamento} onChange={(e) => atualizarData(item.id, 'hora_agendamento', e.target.value)} />
                  </div>
                  {tipoAgendamento === 'pacote' && (
                    <button type="button" className="bg-red-50 text-red-500 border-none h-11 w-full sm:w-11 rounded-xl cursor-pointer flex items-center justify-center transition-colors hover:bg-red-100 shrink-0 mt-1 sm:mt-0 sm:mb-[2px]" onClick={() => removerData(item.id)} disabled={datas.length === 1}><Trash2 size={16} /></button>
                  )}
                </div>
              ))}
              {tipoAgendamento === 'pacote' && (
                <button type="button" className="bg-transparent text-[#6D28D9] border border-dashed border-[#6D28D9]/30 py-3 rounded-xl w-full text-xs font-black cursor-pointer mt-1 hover:bg-[#F8F7FF] transition-colors" onClick={adicionarData}><Plus size={14} /> Adicionar Sessão</button>
              )}
            </div>

            <div className="bg-[#ecfdf5] border border-dashed border-emerald-300 p-4 sm:p-5 rounded-2xl mb-6 w-full box-border">
              <div className="text-[10px] font-black text-[#059669] uppercase tracking-wider mb-2 flex items-center gap-1.5"><MessageSquare size={12} /> Preview</div>
              <p className="text-sm font-medium text-[#065f46] line-height-[1.5] italic m-0 break-words pre-wrap">"{obterTextoPreview()}"</p>
            </div>

            <button type="submit" className={`w-full h-14 border-none rounded-2xl text-white text-sm font-black uppercase tracking-wider flex items-center justify-center gap-2.5 cursor-pointer transition-all duration-300 active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed ${sucesso ? 'bg-emerald-500 shadow-emerald-500/20' : 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:opacity-90 hover:-translate-y-0.5 shadow-lg shadow-[#6D28D9]/20'}`} disabled={loading || sucesso || atingiuLimite}>
              {loading ? <Loader2 size={16} className="animate-spin" /> : sucesso ? <CheckCircle2 size={16} /> : atingiuLimite ? <ShieldAlert size={16} /> : <Send size={16} />}
              {loading ? "Processando..." : sucesso ? "Agendado!" : atingiuLimite ? "Limite Atingido" : "Agendar Disparos"}
            </button>
          </fieldset>
        </form>
      </div>

      {/* SEÇÃO DO HISTÓRICO DE LOGS (oculta no modo compacto do modal por cliente) */}
      {!modoCompacto && (
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.05)] text-left">
        <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
          <div>
            <h2 className="flex items-center gap-2.5 text-lg font-black text-slate-900 m-0"><List size={20} className="text-[#1883FF]" /> Histórico</h2>
            <p className="text-xs text-slate-400 font-medium mt-1">Status dos últimos agendamentos criados.</p>
          </div>
          <div className="flex flex-col gap-1.5 w-full sm:w-auto">
            <label className="text-[10px] font-black uppercase text-slate-400 flex items-center gap-1.5"><Filter size={12} /> Filtrar Data</label>
            <input type="date" className="bg-[#F8F7FF] border border-purple-50 py-2 px-3 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:border-[#6D28D9]" value={filtroData} onChange={(e) => setFiltroData(e.target.value)} />
          </div>
        </div>
        {loadingLista ? (
          <div className="text-center p-10 text-slate-400 text-xs font-bold flex flex-col items-center gap-2"><Loader2 className="animate-spin text-[#1883FF]" /> Carregando Histórico...</div>
        ) : lembretesFiltrados.length === 0 ? (
          <div className="text-center p-10 text-slate-400 text-xs font-bold italic">Nenhum registro encontrado.</div>
        ) : (
          <div className="w-full overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-slate-50/80 border-b-2 border-slate-200">
                  <th className="text-left p-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Data/Hora</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Cliente</th>
                  <th className="text-left p-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">Serviço</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {lembretesFiltrados.map((item) => (
                  <tr key={item.id} className="hover:bg-[#F5F3FF]/50 transition-colors">
                    <td className="p-4"><StatusBadge enviado={item.enviado} status={item.status} dataHoraDisparo={item.data_hora_disparo} /></td>
                    <td className="p-4 text-xs font-bold text-slate-700">{new Date(item.data_hora_disparo).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="p-4 text-xs font-black text-slate-900">{item.nome_cliente || 'Cliente'}</td>
                    <td className="p-4 text-xs font-bold text-slate-600">{item.servico}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default CriarLembreteAvulso;